import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Calendar as CalendarIcon, Clock, User, CheckCircle2,
  ChevronLeft, ChevronRight, ArrowLeft, ArrowRight,
  Headphones, Info, Activity, Star, Building2, Check, Sparkles
} from "lucide-react";

import DrPriyaAvatar from "@/assets/avatars/dr_priya.jpg";
import DrAmitAvatar from "@/assets/avatars/dr_amit.jpg";
import DrNehaAvatar from "@/assets/avatars/dr_neha.jpg";

const getDoctorAvatar = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("priya") || n.includes("sharma")) return DrPriyaAvatar;
  if (n.includes("amit") || n.includes("smith") || n.includes("deep") || n.includes("kusagradhi")) return DrAmitAvatar;
  if (n.includes("neha") || n.includes("sarah")) return DrNehaAvatar;
  return DrPriyaAvatar;
};

const getDoctorDegree = (doc) => {
  if (doc.degree) return doc.degree;
  if (doc.specialization?.includes("MBBS")) return "MBBS, MD";
  if (doc.specialization?.includes("Cardio")) return "MBBS, MD, DM Cardio";
  return "MBBS, MD";
};

const getDoctorTags = (doc) => {
  const s = (doc.specialization || "").toLowerCase();
  if (s.includes("cardio")) return ["Cardiology", "Heart Health", "Hypertension"];
  if (s.includes("neuro")) return ["Neurology", "Spine Care", "Brain Health"];
  if (s.includes("gynec") || s.includes("obstet")) return ["Women's Health", "Prenatal", "Obstetrics"];
  return ["General Medicine", "Diabetes Care", "Preventive Health"];
};

export default function BookAppointment() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [changeDoctorOpen, setChangeDoctorOpen] = useState(false);

  // Stepper state: 1: Doctor, 2: Date, 3: Time, 4: Review
  const [step, setStep] = useState(2);

  // Calendar & Date State (Default to Mon, 8 Sep 2026 or current/next available)
  const [viewYear, setViewYear] = useState(2026);
  const [viewMonth, setViewMonth] = useState(8); // 8 = September (0-indexed)
  const [selectedDate, setSelectedDate] = useState("2026-09-08");

  // Slots State
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("10:30 AM");

  // Booking form details
  const [visitType, setVisitType] = useState("New Consultation");
  const [complaint, setComplaint] = useState("");
  const [booking, setBooking] = useState(false);
  const [bookedAppt, setBookedAppt] = useState(null);

  // 1. Fetch available doctors
  useEffect(() => {
    api.get("/portal/doctors")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const filtered = list.filter((d) => d.is_available !== false);
        setDoctors(filtered);
        if (filtered.length > 0 && !selectedDoctorId) {
          // Prefer Dr. Priya Sharma if available, otherwise first doctor
          const priya = filtered.find((d) => d.name?.toLowerCase().includes("priya"));
          setSelectedDoctorId(priya ? (priya.id || priya._id) : (filtered[0].id || filtered[0]._id));
        }
      })
      .catch((err) => toast({ title: "Error", description: formatError(err), variant: "destructive" }));
  }, [toast, selectedDoctorId]);

  // 2. Fetch slots when doctor or date changes
  useEffect(() => {
    if (!selectedDoctorId || !selectedDate) {
      setSlots(null);
      return;
    }
    setLoadingSlots(true);
    api.get(`/doctors/${selectedDoctorId}/available-slots?date=${selectedDate}`)
      .then((res) => {
        setSlots(res.data);
        if (res.data?.slots?.length > 0) {
          // Keep current selected slot if valid, otherwise pick first free slot
          const freeSlot = res.data.slots.find((s) => !s.is_full);
          if (freeSlot) {
            setSelectedSlot(freeSlot.start);
          }
        }
      })
      .catch(() => setSlots(null))
      .finally(() => setLoadingSlots(false));
  }, [selectedDoctorId, selectedDate]);

  const selectedDoctor = doctors.find((d) => (d.id || d._id) === selectedDoctorId) || doctors[0] || {
    name: "Dr. Priya Sharma",
    specialization: "General Physician",
    room_number: "102",
    consultation_fee: 1200,
    rating: 4.8,
    review_count: 124,
  };

  const handleBook = async () => {
    if (!selectedDoctorId || !selectedDate || !selectedSlot) {
      toast({ title: "Missing details", description: "Select a doctor, date, and slot", variant: "destructive" });
      return;
    }
    setBooking(true);
    try {
      const res = await api.post("/appointments/self-book", {
        doctor_id: selectedDoctorId,
        visit_date: selectedDate,
        slot_time: selectedSlot,
        complaint: complaint || "General Consultation",
        visit_type: visitType,
      });
      const data = res.data?.data || res.data;
      setBookedAppt(data);
      toast({ title: "Appointment booked successfully!", description: `Token #${data.token_number || "3"}` });
    } catch (err) {
      toast({ title: "Booking failed", description: formatError(err), variant: "destructive" });
    } finally {
      setBooking(false);
    }
  };

  // Calendar Helpers
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  // Generate calendar grid for viewYear and viewMonth
  const generateCalendarDays = () => {
    const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay(); // 0 = Sun, 1 = Mon ...
    // Shift so Mon = 0, Sun = 6
    const adjustedStart = (firstDayIndex + 6) % 7;
    const totalDays = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

    const days = [];

    // Preceding month trailing days
    for (let i = adjustedStart - 1; i >= 0; i--) {
      days.push({
        dayNumber: prevMonthDays - i,
        isCurrentMonth: false,
        dateStr: "",
        isAvailable: false,
      });
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const dStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayOfWeek = new Date(viewYear, viewMonth, day).getDay();
      // Sundays (0) unavailable
      const isAvailable = dayOfWeek !== 0;

      days.push({
        dayNumber: day,
        isCurrentMonth: true,
        dateStr: dStr,
        isAvailable,
      });
    }

    // Trailing days for grid completion
    const remaining = 35 - days.length > 0 ? 35 - days.length : (42 - days.length > 0 ? 42 - days.length : 0);
    for (let i = 1; i <= remaining; i++) {
      days.push({
        dayNumber: i,
        isCurrentMonth: false,
        dateStr: "",
        isAvailable: false,
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Quick date chips
  const quickDates = [
    { label: "Tue, 9 Sep 2026", dateStr: "2026-09-09" },
    { label: "Wed, 10 Sep 2026", dateStr: "2026-09-10" },
    { label: "Thu, 11 Sep 2026", dateStr: "2026-09-11" },
    { label: "Fri, 12 Sep 2026", dateStr: "2026-09-12" },
  ];

  // Format date readable
  const formatDisplayDate = (dStr) => {
    if (!dStr) return "Mon, 8 Sep 2026";
    const [y, m, d] = dStr.split("-").map(Number);
    const dateObj = new Date(y, m - 1, d);
    return dateObj.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  };

  // Mock morning/afternoon slots if slots API returns empty or custom slots
  const defaultSlots = [
    "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
    "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM"
  ];
  const activeSlotList = slots?.slots?.length ? slots.slots.map((s) => s.start) : defaultSlots;

  if (bookedAppt) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center" data-testid="booking-success">
        <div className="bg-white border border-emerald-100 rounded-3xl p-8 shadow-sm">
          <div className="w-16 h-16 bg-[#eaf8f0] text-[#0c7a5f] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={36} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 font-sans">Booking Confirmed!</h2>
          <p className="text-sm text-slate-500 mt-1">Your appointment has been successfully scheduled</p>

          <div className="mt-6 bg-[#f8faf9] rounded-2xl p-5 text-left space-y-3.5 border border-slate-100">
            <div className="flex justify-between border-b border-slate-200/60 pb-2.5">
              <span className="text-xs text-slate-500 font-semibold uppercase">Token Number</span>
              <span className="font-extrabold text-[#0c7a5f] text-lg">#{bookedAppt.token_number || "3"}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Doctor</span>
              <span className="font-bold text-slate-900">{formatDoctorName(selectedDoctor.name)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Specialization</span>
              <span className="font-semibold text-slate-700">{selectedDoctor.specialization}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Date & Slot</span>
              <span className="font-semibold text-slate-900">{formatDisplayDate(selectedDate)} at {selectedSlot}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Room & Type</span>
              <span className="font-semibold text-slate-900">Room {selectedDoctor.room_number || "102"} • {visitType}</span>
            </div>
          </div>

          <div className="flex gap-3 mt-6 justify-center">
            <Button
              variant="outline"
              onClick={() => {
                setBookedAppt(null);
                setStep(2);
              }}
              className="rounded-xl text-xs font-semibold px-5"
            >
              Book Another
            </Button>
            <Button
              className="bg-[#0c7a5f] hover:bg-[#0a664e] text-white rounded-xl text-xs font-semibold px-5"
              onClick={() => navigate("/dashboard")}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* 1. TOP HEADER & STEPPER (Matching Image 1) */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 transition -ml-1 mt-0.5"
            title="Back to Dashboard"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-sans tracking-tight">
              Book an Appointment
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Choose your doctor, date and time, and confirm your visit
            </p>
          </div>
        </div>

        {/* Stepper with Heartbeat Pulse Badge on the Right */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-slate-200/80 shadow-2xs">
            {/* Step 1: Doctor */}
            <div
              onClick={() => setStep(1)}
              className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-[#0c7a5f]"
            >
              <div className="w-5 h-5 rounded-full bg-[#0c7a5f] text-white flex items-center justify-center text-[10px]">
                <Check size={12} strokeWidth={3} />
              </div>
              <span>Doctor</span>
            </div>

            <div className="w-5 h-[1.5px] bg-[#0c7a5f]" />

            {/* Step 2: Date */}
            <div
              onClick={() => setStep(2)}
              className={`flex items-center gap-1.5 cursor-pointer text-xs font-semibold ${
                step >= 2 ? "text-[#0c7a5f]" : "text-slate-400"
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                step >= 2 ? "bg-[#0c7a5f] text-white" : "bg-slate-100 text-slate-500"
              }`}>
                2
              </div>
              <span>Date</span>
            </div>

            <div className={`w-5 h-[1.5px] ${step >= 3 ? "bg-[#0c7a5f]" : "bg-slate-200"}`} />

            {/* Step 3: Time */}
            <div
              onClick={() => setStep(3)}
              className={`flex items-center gap-1.5 cursor-pointer text-xs font-semibold ${
                step >= 3 ? "text-[#0c7a5f]" : "text-slate-400"
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                step >= 3 ? "bg-[#0c7a5f] text-white" : "bg-slate-100 text-slate-500"
              }`}>
                3
              </div>
              <span>Time</span>
            </div>

            <div className={`w-5 h-[1.5px] ${step >= 4 ? "bg-[#0c7a5f]" : "bg-slate-200"}`} />

            {/* Step 4: Review */}
            <div
              onClick={() => setStep(4)}
              className={`flex items-center gap-1.5 cursor-pointer text-xs font-semibold ${
                step === 4 ? "text-[#0c7a5f]" : "text-slate-400"
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                step === 4 ? "bg-[#0c7a5f] text-white" : "bg-slate-100 text-slate-500"
              }`}>
                4
              </div>
              <span>Review</span>
            </div>
          </div>

          {/* Heartbeat EKG Pulse Badge */}
          <div className="w-9 h-9 rounded-xl bg-[#eaf8f0] text-[#0c7a5f] flex items-center justify-center shadow-2xs">
            <Activity size={18} />
          </div>
        </div>
      </div>

      {/* 2. MAIN 2-COLUMN BOOKING CONTAINER (Matching Image 1) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* LEFT COLUMN: Steps Accordion / Content */}
        <div className="lg:col-span-8 space-y-4">
          {/* STEP 1: Select Doctor Card (Completed Summary) */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 rounded-full bg-[#0c7a5f] text-white flex items-center justify-center text-[10px]">
                <Check size={12} strokeWidth={3} />
              </div>
              <span className="text-xs font-bold text-slate-900 font-sans">1 Select Doctor</span>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-1">
              <div className="flex items-center gap-4">
                <img
                  src={getDoctorAvatar(selectedDoctor.name)}
                  alt={selectedDoctor.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-emerald-100 flex-shrink-0"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-slate-900">{formatDoctorName(selectedDoctor.name)}</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{selectedDoctor.specialization || "General Physician"}</p>

                  <div className="flex items-center gap-2.5 mt-1 text-xs text-slate-500">
                    <span className="font-medium text-slate-600">{getDoctorDegree(selectedDoctor)}</span>
                    <span className="text-slate-300">•</span>
                    <span className="flex items-center gap-1 text-amber-500 font-semibold">
                      <Star size={12} fill="currentColor" />
                      <span>{selectedDoctor.rating || "4.8"}</span>
                      <span className="text-slate-400 font-normal">({selectedDoctor.review_count || "124"} reviews)</span>
                    </span>
                  </div>

                  {/* Specialty Pills */}
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {getDoctorTags(selectedDoctor).map((tag, idx) => (
                      <span
                        key={idx}
                        className="text-[11px] font-medium bg-[#eef8f3] text-[#0c7a5f] px-2.5 py-0.5 rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mt-2.5 text-xs text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Building2 size={13} className="text-slate-400" />
                      <span>Room {selectedDoctor.room_number || "102"}</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-900 font-bold">
                      <span>₹{selectedDoctor.consultation_fee || "1200"}</span>
                      <span className="text-[11px] text-slate-400 font-normal">Consultation Fee</span>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setChangeDoctorOpen(true)}
                className="text-xs font-semibold text-[#0c7a5f] border-slate-200 hover:border-[#0c7a5f] hover:bg-[#eaf8f0] rounded-xl px-4 py-2 self-end sm:self-center"
              >
                Change Doctor
              </Button>
            </div>
          </div>

          {/* STEP 2: Select Date (Interactive Calendar & Quick Chips) */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 rounded-full bg-[#0c7a5f] text-white flex items-center justify-center text-[11px] font-bold">
                2
              </div>
              <span className="text-xs font-bold text-slate-900 font-sans">Select Date</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 pt-1">
              {/* Left Sub-Column: Calendar Widget (Matching Image 1) */}
              <div className="md:col-span-7 border-r border-slate-100 pr-0 md:pr-6">
                {/* Month Navigator Header */}
                <div className="flex items-center justify-between mb-4 px-2">
                  <button
                    onClick={handlePrevMonth}
                    className="p-1 rounded-lg hover:bg-slate-100 text-slate-600 transition"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm font-bold text-slate-900 font-sans">
                    {monthNames[viewMonth]} {viewYear}
                  </span>
                  <button
                    onClick={handleNextMonth}
                    className="p-1 rounded-lg hover:bg-slate-100 text-slate-600 transition"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>

                {/* Day of Week Headers */}
                <div className="grid grid-cols-7 text-center text-xs font-semibold text-slate-500 mb-2">
                  <span>Mon</span>
                  <span>Tue</span>
                  <span>Wed</span>
                  <span>Thu</span>
                  <span>Fri</span>
                  <span>Sat</span>
                  <span>Sun</span>
                </div>

                {/* Day Cells Grid */}
                <div className="grid grid-cols-7 gap-y-2 text-center text-xs">
                  {calendarDays.map((d, index) => {
                    if (!d.isCurrentMonth) {
                      return (
                        <div key={index} className="py-2 text-slate-300 select-none">
                          {d.dayNumber}
                        </div>
                      );
                    }

                    const isSelected = selectedDate === d.dateStr;

                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (d.isAvailable) {
                            setSelectedDate(d.dateStr);
                          }
                        }}
                        className={`flex flex-col items-center justify-center py-1 cursor-pointer transition ${
                          !d.isAvailable ? "cursor-not-allowed opacity-50" : ""
                        }`}
                      >
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center font-medium transition ${
                            isSelected
                              ? "bg-[#0c7a5f] text-white font-bold shadow-xs scale-105"
                              : "text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          {d.dayNumber}
                        </div>

                        {/* Availability Dot Indicator */}
                        <div
                          className={`w-1 h-1 rounded-full mt-1 ${
                            isSelected
                              ? "bg-[#0c7a5f]"
                              : d.isAvailable
                              ? "bg-emerald-500"
                              : "bg-slate-300"
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Legend at Bottom of Calendar */}
                <div className="flex items-center justify-center gap-4 mt-6 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span>Available</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-slate-300" />
                    <span>Unavailable</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#0c7a5f]" />
                    <span>Selected</span>
                  </div>
                </div>
              </div>

              {/* Right Sub-Column: Available On Badge & Quick Chips */}
              <div className="md:col-span-5 flex flex-col justify-between space-y-4">
                <div className="space-y-4">
                  {/* Selected Date Badge Banner */}
                  <div className="p-3 rounded-xl bg-[#f0f9f4] border border-emerald-100/80 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white text-[#0c7a5f] flex items-center justify-center shadow-2xs flex-shrink-0">
                      <CalendarIcon size={16} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-medium">Available on</p>
                      <p className="text-xs font-bold text-[#0c7a5f]">{formatDisplayDate(selectedDate)}</p>
                    </div>
                  </div>

                  {/* Next Available Dates Chips */}
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-2">Next available dates</p>
                    <div className="grid grid-cols-2 gap-2">
                      {quickDates.map((qd) => (
                        <button
                          key={qd.dateStr}
                          onClick={() => setSelectedDate(qd.dateStr)}
                          className={`px-3 py-2 rounded-xl text-xs font-medium border text-left transition ${
                            selectedDate === qd.dateStr
                              ? "border-[#0c7a5f] bg-[#eaf8f0] text-[#0c7a5f] font-semibold"
                              : "border-slate-200 bg-white hover:border-emerald-200 text-slate-700"
                          }`}
                        >
                          {qd.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 30 Days in Advance Notice Banner */}
                <div className="p-3 rounded-xl bg-[#f0f9f7] border border-emerald-100 flex items-start gap-2.5 text-xs text-slate-600">
                  <Info size={15} className="text-[#0c7a5f] flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-relaxed">
                    You can book appointments up to <span className="font-bold text-slate-800">30 days in advance</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* STEP 3: Select Time Slots (Collapsible / Viewable) */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  selectedSlot ? "bg-[#0c7a5f] text-white" : "bg-slate-200 text-slate-600"
                }`}>
                  3
                </div>
                <span className="text-xs font-bold text-slate-900 font-sans">Select Time Slot</span>
              </div>
              <span className="text-xs text-slate-400">
                {selectedSlot ? `Selected: ${selectedSlot}` : "Choose a slot"}
              </span>
            </div>

            {loadingSlots ? (
              <div className="py-4 text-center text-xs text-slate-400">Loading available slots...</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 pt-1">
                {activeSlotList.map((slotTime) => {
                  const isSelected = selectedSlot === slotTime;
                  return (
                    <button
                      key={slotTime}
                      onClick={() => {
                        setSelectedSlot(slotTime);
                        setStep(3);
                      }}
                      className={`py-2 px-2 rounded-xl text-xs font-semibold text-center transition ${
                        isSelected
                          ? "bg-[#0c7a5f] text-white shadow-xs font-bold"
                          : "border border-slate-200 bg-white hover:border-emerald-300 text-slate-700"
                      }`}
                    >
                      {slotTime}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* STEP 4: Review Details (Visit Type & Reason) */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[11px] font-bold">
                4
              </div>
              <span className="text-xs font-bold text-slate-900 font-sans">Review & Consultation Details</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Visit Type</Label>
                <select
                  value={visitType}
                  onChange={(e) => setVisitType(e.target.value)}
                  className="w-full mt-1.5 border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-800 focus:ring-1 focus:ring-emerald-500 focus:outline-none"
                >
                  <option value="New Consultation">New Consultation</option>
                  <option value="Follow-up">Follow-up Visit</option>
                  <option value="Routine Checkup">Routine Health Checkup</option>
                </select>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700">Reason / Chief Complaint (Optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g. Fever, routine diabetes review"
                  value={complaint}
                  onChange={(e) => setComplaint(e.target.value)}
                  className="mt-1.5 text-xs rounded-xl border-slate-200"
                />
              </div>
            </div>
          </div>

          {/* BOTTOM ACTIONS BAR (Matching Image 1) */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="border-slate-200 text-slate-700 rounded-xl px-5 py-2 text-xs font-semibold hover:bg-slate-50 gap-2"
            >
              <ArrowLeft size={14} /> Back
            </Button>

            <Button
              onClick={handleBook}
              disabled={booking || !selectedSlot}
              className="bg-[#0c7a5f] hover:bg-[#0a664e] text-white rounded-xl px-6 py-2.5 text-xs font-semibold gap-2 shadow-xs transition"
            >
              {booking ? (
                "Confirming..."
              ) : (
                <>
                  <span>Next: Select Time</span>
                  <ArrowRight size={14} />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* RIGHT COLUMN: Appointment Summary & Need Help? (Matching Image 1) */}
        <div className="lg:col-span-4 space-y-4">
          {/* Card 1: Appointment Summary */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs">
            <div className="flex items-center gap-2 pb-3.5 border-b border-slate-100">
              <div className="w-6 h-6 rounded-lg bg-[#eaf8f0] text-[#0c7a5f] flex items-center justify-center">
                <CalendarIcon size={14} />
              </div>
              <h3 className="text-sm font-bold text-slate-900 font-sans">Appointment Summary</h3>
            </div>

            <div className="pt-4 space-y-4">
              {/* Doctor Row */}
              <div className="flex items-center gap-3.5">
                <img
                  src={getDoctorAvatar(selectedDoctor.name)}
                  alt={selectedDoctor.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-emerald-100"
                />
                <div>
                  <h4 className="text-xs font-bold text-slate-900">{formatDoctorName(selectedDoctor.name)}</h4>
                  <p className="text-[11px] text-slate-500">{selectedDoctor.specialization || "General Physician"}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Room {selectedDoctor.room_number || "102"} • ₹{selectedDoctor.consultation_fee || "1200"}
                  </p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-3 space-y-3">
                {/* Date */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#f0f9f4] text-[#0c7a5f] flex items-center justify-center flex-shrink-0">
                    <CalendarIcon size={14} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Date</p>
                    <p className="text-xs font-bold text-slate-800">{formatDisplayDate(selectedDate)}</p>
                  </div>
                </div>

                {/* Time Slot */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#f0f9f4] text-[#0c7a5f] flex items-center justify-center flex-shrink-0">
                    <Clock size={14} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Time Slot</p>
                    <p className="text-xs font-bold text-slate-800">
                      {selectedSlot || "Select time slot"}
                    </p>
                  </div>
                </div>

                {/* Visit Type */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#f0f9f4] text-[#0c7a5f] flex items-center justify-center flex-shrink-0">
                    <User size={14} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Visit Type</p>
                    <p className="text-xs font-bold text-slate-800">{visitType}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Need Help? Box */}
          <div className="bg-[#f7fbf9] rounded-2xl border border-emerald-100/70 p-4.5 flex items-start gap-3.5 shadow-2xs">
            <div className="w-9 h-9 rounded-xl bg-white border border-emerald-100 flex items-center justify-center text-[#0c7a5f] flex-shrink-0 shadow-2xs">
              <Headphones size={18} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900">Need help?</h4>
              <p className="text-xs font-semibold text-[#0c7a5f] mt-0.5">Call us at +91 98765 43210</p>
              <p className="text-[10px] text-slate-400 mt-0.5">We're available 8 AM - 8 PM</p>
            </div>
          </div>
        </div>
      </div>

      {/* CHANGE DOCTOR DIALOG */}
      <Dialog open={changeDoctorOpen} onOpenChange={setChangeDoctorOpen}>
        <DialogContent className="max-w-xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold font-sans">Choose a Doctor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2 max-h-96 overflow-y-auto">
            {doctors.map((d) => {
              const docId = d.id || d._id;
              const isCurrent = docId === selectedDoctorId;
              return (
                <div
                  key={docId}
                  onClick={() => {
                    setSelectedDoctorId(docId);
                    setChangeDoctorOpen(false);
                  }}
                  className={`p-3.5 rounded-xl border flex items-center justify-between cursor-pointer transition ${
                    isCurrent
                      ? "border-[#0c7a5f] bg-[#eaf8f0]"
                      : "border-slate-200 bg-white hover:border-emerald-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={getDoctorAvatar(d.name)}
                      alt={d.name}
                      className="w-12 h-12 rounded-full object-cover border"
                    />
                    <div>
                      <p className="text-xs font-bold text-slate-900">{formatDoctorName(d.name)}</p>
                      <p className="text-[11px] text-slate-500">{d.specialization}</p>
                      <p className="text-[10px] text-slate-400">Room {d.room_number || "101"} • Fee: ₹{d.consultation_fee || "500"}</p>
                    </div>
                  </div>

                  {isCurrent && (
                    <span className="text-xs font-semibold text-[#0c7a5f] flex items-center gap-1">
                      <Check size={14} /> Selected
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
